import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Check, X } from 'lucide-react';

import ReachabilityBadge from '../../atoms/ReachabilityBadge';
import { REACH_LEVELS } from '../../atoms/ReachabilityIndicator';

// Figma "Reachability popover" — explains why a contact lands at a reachability
// level. Thresholds mirror the design: a level is reached when AT LEAST ONE of
// its conditions is met, evaluated against the team's connection counts to the
// contact. `none` has no conditions (and no "See connections" affordance).
const LEVEL_CONFIG = {
  high: {
    description: 'Your team has strong connections to this person. A warm intro is very likely to land.',
    conditions: [
      { label: '1 or more Warm connections', isMet: ({ warm }) => warm >= 1 },
      { label: '3 or more Known connections', isMet: ({ known }) => known >= 3 },
      { label: '10 or more Cold connections', isMet: ({ cold }) => cold >= 10 },
    ],
  },
  medium: {
    description: 'Someone on your team has a connection to this person. A warm intro is plausible.',
    conditions: [
      { label: '1 or more Known connections', isMet: ({ known }) => known >= 1 },
      { label: '5 or more Cold connections', isMet: ({ cold }) => cold >= 5 },
    ],
  },
  low: {
    description: "Your team's connections to this person are weak or indirect. A warm intro might be hard.",
    conditions: [
      { label: '1 or more Cold connections', isMet: ({ cold }) => cold >= 1 },
      // A 2-hop route is the other way into Low, and for most contacts at this
      // level it is the only one. Without it the panel showed a single unmet
      // condition under a "Low" badge, which reads as a contradiction.
      { label: '1 or more Inferred connections (through a mutual contact)', isMet: ({ inferred }) => inferred >= 1 },
    ],
  },
  none: {
    description: 'Your team has no connections to this person.',
    conditions: [],
  },
};

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 360px;
  max-width: 100%;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.neutral[0]};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: 4px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
`;

const Description = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const ConditionsTitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

const ConditionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ConditionRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme, $met }) => ($met ? theme.colors.text.dark : theme.colors.text.subtle)};
`;

const MetIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.green[600]};
  color: ${({ theme }) => theme.colors.neutral[0]};
`;

const UnmetIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

const SeeConnections = styled.button`
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.dark};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: 4px;
  padding: 10px 12px;
  cursor: pointer;
  &:disabled {
    cursor: default;
  }
`;

const ReachabilityPopover = ({ level, counts, total, onSeeConnections, className }) => {
  const normalizedLevel = REACH_LEVELS.includes(level) ? level : 'none';
  const config = LEVEL_CONFIG[normalizedLevel];
  // Mirror the Connections column (`reachability.total ?? sum(counts)`) so the
  // hover CTA and the sortable column never disagree for the same contact.
  const resolvedTotal = total ?? (counts.warm ?? 0) + (counts.known ?? 0) + (counts.cold ?? 0);

  return (
    <Panel className={className} data-reachability={normalizedLevel} role="tooltip">
      <ReachabilityBadge level={normalizedLevel} />
      <Description>{config.description}</Description>

      {config.conditions.length > 0 && (
        <>
          <ConditionsTitle>Because at least one of these is met:</ConditionsTitle>
          <ConditionList>
            {config.conditions.map((condition) => {
              const met = condition.isMet(counts);
              return (
                <ConditionRow key={condition.label} $met={met} data-met={met}>
                  {met ? (
                    <MetIcon aria-label="met">
                      <Check size={12} strokeWidth={3} aria-hidden="true" />
                    </MetIcon>
                  ) : (
                    <UnmetIcon aria-label="not met">
                      <X size={16} strokeWidth={2} aria-hidden="true" />
                    </UnmetIcon>
                  )}
                  {condition.label}
                </ConditionRow>
              );
            })}
          </ConditionList>
        </>
      )}

      {/* `resolvedTotal` counts direct connections only, so a contact that is
          Low purely on a 2-hop route has none to show. Offering "See 0
          connections" was worse than offering nothing. */}
      {normalizedLevel !== 'none' && resolvedTotal > 0 && (
        <SeeConnections type="button" onClick={onSeeConnections} disabled={!onSeeConnections}>
          See {resolvedTotal} {resolvedTotal === 1 ? 'connection' : 'connections'}
        </SeeConnections>
      )}
    </Panel>
  );
};

ReachabilityPopover.propTypes = {
  level: PropTypes.oneOf(REACH_LEVELS).isRequired,
  counts: PropTypes.shape({
    warm: PropTypes.number,
    known: PropTypes.number,
    cold: PropTypes.number,
    inferred: PropTypes.number,
  }),
  total: PropTypes.number,
  onSeeConnections: PropTypes.func,
  className: PropTypes.string,
};

ReachabilityPopover.defaultProps = {
  counts: {},
  total: undefined,
  onSeeConnections: undefined,
  className: undefined,
};

export { LEVEL_CONFIG };
export default ReachabilityPopover;
